import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixMembersV3Follow from './members-v3-follow-member-followers.http.js';

/**
 * A follower represents a connection between 2 members, where 1 member follows another.
 * This relationship enables members to track updates and activities from members they follow, creating a social network on the site.
 */
export interface Follower {
  /**
   * Member ID of the member who performed the action.
   * @format GUID
   */
  memberId?: string;
  /**
   * Member ID of the member being followed or unfollowed.
   * @format GUID
   */
  affectedMemberId?: string;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface FollowMemberRequest {
  /**
   * ID of the member to follow.
   * @format GUID
   */
  memberId: string;
}

export interface FollowMemberResponse {}

export interface MemberFollowed {
  /** Member who is following the other member. */
  memberConnection?: Follower;
}

export interface UnfollowMemberRequest {
  /**
   * ID of the member to unfollow.
   * @format GUID
   */
  memberId: string;
}

export interface UnfollowMemberResponse {}

export interface MemberUnfollowed {
  /** Member who is unfollowing the other member. */
  memberConnection?: Follower;
}

export interface ListMyMemberFollowingRequest {
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface CursorPaging {
  /**
   * Number of items to return. See [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   * @min 1
   * @max 100
   */
  limit?: number | null;
  /** Cursor returned from last query response. */
  cursor?: string | null;
}

export interface ListMyMemberFollowingResponse {
  /**
   * Retrieved list of members followed by the current member.
   * @format GUID
   */
  memberIds?: string[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items starting from given cursor. */
  count?: number | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface ListMemberFollowingRequest {
  /**
   * ID of the member whose followers to list.
   * @format GUID
   */
  memberId: string;
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface ListMemberFollowingResponse {
  /**
   * Retrieved list of members who are followed by the given member.
   * @format GUID
   */
  memberIds?: string[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PagingMetadataV2;
}

export interface ListMyMemberFollowersRequest {
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface ListMyMemberFollowersResponse {
  /**
   * Retrieved list of members who are following the current member.
   * @format GUID
   */
  memberIds?: string[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PagingMetadataV2;
}

export interface ListMemberFollowersRequest {
  /**
   * ID of the member whose followed members to list.
   * @format GUID
   */
  memberId: string;
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface ListMemberFollowersResponse {
  /**
   * Retrieved list of members who are following the given member.
   * @format GUID
   */
  memberIds?: string[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PagingMetadataV2;
}

export interface QueryMyMemberConnectionsRequest {
  /**
   * List of member IDs whose connections to the current member will be retrieved.
   * @format GUID
   * @maxSize 100
   */
  connectedMemberIds: string[];
}

export interface QueryMyMemberConnectionsResponse {
  /** Retrieved list of members whose connections to the current member were retrieved. */
  connectedMembers?: ConnectedMembers[];
}

export interface ConnectedMembers {
  /**
   * Site member ID.
   * @format GUID
   */
  connectedMemberId?: string;
  /** Whether the listed member is followed by the given member. */
  followedByMember?: boolean;
  /** Whether the listed member follows the given member. */
  followingMember?: boolean;
}

export interface QueryMemberConnectionsRequest {
  /**
   * List of member IDs whose connections to the given member will be retrieved.
   * @format GUID
   * @maxSize 100
   */
  connectedMemberIds: string[];
  /**
   * Member ID.
   * @format GUID
   */
  memberId: string;
}

export interface QueryMemberConnectionsResponse {
  /** Retrieved list of members whose connections to the given member were retrieved. */
  connectedMembers?: ConnectedMembers[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface FollowMemberFollowedEnvelope {
  data: MemberFollowed;
  metadata: EventMetadata;
}

/**
 * Triggered when a member follows another member.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Members
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-MEMBERS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Member Connections
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-FOLLOWERS
 * @permissionId MEMBERS.FOLLOW_READ
 * @webhook
 * @eventType wix.members.v3.follow_member_followed
 * @serviceIdentifier com.wixpress.members.follow.v3.MemberFollowers
 * @slug member_followed
 */
export declare function onFollowMemberFollowed(
  handler: (event: FollowMemberFollowedEnvelope) => void | Promise<void>
): void;

export interface FollowMemberUnfollowedEnvelope {
  data: MemberUnfollowed;
  metadata: EventMetadata;
}

/**
 * Triggered when a member unfollows another member.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Members
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-MEMBERS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Member Connections
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-FOLLOWERS
 * @permissionId MEMBERS.FOLLOW_READ
 * @webhook
 * @eventType wix.members.v3.follow_member_unfollowed
 * @serviceIdentifier com.wixpress.members.follow.v3.MemberFollowers
 * @slug member_unfollowed
 */
export declare function onFollowMemberUnfollowed(
  handler: (event: FollowMemberUnfollowedEnvelope) => void | Promise<void>
): void;

/**
 * Sets the current member to follow another, specified member.
 * @param memberId - ID of the member to follow.
 * @public
 * @requiredField memberId
 * @permissionId MEMBERS.MEMBER_FOLLOW
 * @applicableIdentity MEMBER
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.FollowMember
 */
export async function followMember(memberId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ memberId: memberId });

  const reqOpts = ambassadorWixMembersV3Follow.followMember(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { memberId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['memberId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Sets the current member to unfollow another, specified member.
 * @param memberId - ID of the member to unfollow.
 * @public
 * @requiredField memberId
 * @permissionId MEMBERS.MEMBER_FOLLOW
 * @applicableIdentity MEMBER
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.UnfollowMember
 */
export async function unfollowMember(memberId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ memberId: memberId });

  const reqOpts = ambassadorWixMembersV3Follow.unfollowMember(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { memberId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['memberId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of members who are followed by the current member.
 * @public
 * @permissionId MEMBERS.FOLLOW_READ_MY
 * @applicableIdentity APP
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.ListMyMemberFollowing
 */
export async function listCurrentMemberFollowing(
  options?: ListCurrentMemberFollowingOptions
): Promise<ListCurrentMemberFollowingResult> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paging: options?.paging,
  });

  const reqOpts = ambassadorWixMembersV3Follow.listMyMemberFollowing(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return {
      memberIds: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.memberIds,
      metadata: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.pagingMetadata,
    }!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { paging: '$[0].paging' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListCurrentMemberFollowingOptions {
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface ListCurrentMemberFollowingResult {
  memberIds: string[];
  metadata: PagingMetadataV2;
}

/**
 * Retrieves a list of members who are followed by the given member.
 * @param memberId - ID of the member whose followers to list.
 * @public
 * @requiredField memberId
 * @permissionId MEMBERS.FOLLOW_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.ListMemberFollowing
 */
export async function listMemberFollowing(
  memberId: string,
  options?: ListMemberFollowingOptions
): Promise<ListMemberFollowingResult> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    memberId: memberId,
    paging: options?.paging,
  });

  const reqOpts = ambassadorWixMembersV3Follow.listMemberFollowing(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return {
      memberIds: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.memberIds,
      metadata: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.pagingMetadata,
    }!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { memberId: '$[0]', paging: '$[1].paging' },
        singleArgumentUnchanged: false,
      },
      ['memberId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListMemberFollowingOptions {
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface ListMemberFollowingResult {
  memberIds: string[];
  metadata: PagingMetadataV2;
}

/**
 * Retrieves a list of members who are following the current member.
 * @public
 * @permissionId MEMBERS.FOLLOW_READ_MY
 * @applicableIdentity APP
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.ListMyMemberFollowers
 */
export async function listCurrentMemberFollowers(
  options?: ListCurrentMemberFollowersOptions
): Promise<ListCurrentMemberFollowersResult> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paging: options?.paging,
  });

  const reqOpts = ambassadorWixMembersV3Follow.listMyMemberFollowers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return {
      memberIds: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.memberIds,
      metadata: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.pagingMetadata,
    }!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { paging: '$[0].paging' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListCurrentMemberFollowersOptions {
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface ListCurrentMemberFollowersResult {
  memberIds: string[];
  metadata: PagingMetadataV2;
}

/**
 * Retrieves a list of members who are following the given member.
 * @param memberId - ID of the member whose followed members to list.
 * @public
 * @requiredField memberId
 * @permissionId MEMBERS.FOLLOW_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.ListMemberFollowers
 */
export async function listMemberFollowers(
  memberId: string,
  options?: ListMemberFollowersOptions
): Promise<ListMemberFollowersResult> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    memberId: memberId,
    paging: options?.paging,
  });

  const reqOpts = ambassadorWixMembersV3Follow.listMemberFollowers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return {
      memberIds: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.memberIds,
      metadata: renameKeysFromRESTResponseToSDKResponse(result.data)
        ?.pagingMetadata,
    }!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { memberId: '$[0]', paging: '$[1].paging' },
        singleArgumentUnchanged: false,
      },
      ['memberId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListMemberFollowersOptions {
  /**
   * Pagination options. For more information, see
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  paging?: CursorPaging;
}

export interface ListMemberFollowersResult {
  memberIds: string[];
  metadata: PagingMetadataV2;
}

/**
 * Retrieves a list of members and their connections to the current member.
 * @param connectedMemberIds - List of member IDs whose connections to the current member will be retrieved.
 * @public
 * @requiredField connectedMemberIds
 * @permissionId MEMBERS.FOLLOW_READ_MY
 * @applicableIdentity APP
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.QueryMyMemberConnections
 */
export async function queryCurrentMemberConnections(
  connectedMemberIds: string[]
): Promise<
  NonNullablePaths<
    QueryMyMemberConnectionsResponse,
    | `connectedMembers`
    | `connectedMembers.${number}.connectedMemberId`
    | `connectedMembers.${number}.followedByMember`
    | `connectedMembers.${number}.followingMember`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    connectedMemberIds: connectedMemberIds,
  });

  const reqOpts =
    ambassadorWixMembersV3Follow.queryMyMemberConnections(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { connectedMemberIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['connectedMemberIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of members and their connections to the given member.
 * > **Note:** If an empty array is passed as `connectedMemberIds`, the call will succeed, but it will not return any data.
 * @param memberId - Member ID.
 * @param connectedMemberIds - List of member IDs whose connections to the given member will be retrieved.
 * @public
 * @requiredField connectedMemberIds
 * @requiredField memberId
 * @permissionId MEMBERS.FOLLOW_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.members.follow.v3.MemberFollowers.QueryMemberConnections
 */
export async function queryMemberConnections(
  memberId: string,
  connectedMemberIds: string[]
): Promise<
  NonNullablePaths<
    QueryMemberConnectionsResponse,
    | `connectedMembers`
    | `connectedMembers.${number}.connectedMemberId`
    | `connectedMembers.${number}.followedByMember`
    | `connectedMembers.${number}.followingMember`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    memberId: memberId,
    connectedMemberIds: connectedMemberIds,
  });

  const reqOpts = ambassadorWixMembersV3Follow.queryMemberConnections(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          memberId: '$[0]',
          connectedMemberIds: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['memberId', 'connectedMemberIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
